import {
  TagPickerFragment,
  TagType,
  TagsQuery,
} from "generated-graphql/graphql";
import { useTenant } from "hooks/useTenant";
import { uniqBy } from "lodash-es";
import { TAGS_QUERY } from "queries/tags";
import React, { forwardRef } from "react";
import { ControllerFieldState } from "react-hook-form";
import { v4 as uuidv4 } from "uuid";
import EncampAutocomplete from "./Forms/EncampAutocomplete";

type Props = {
  onChange: (tags: (TagPickerFragment | string)[]) => void;
  disabled?: boolean;
  value?: TagPickerFragment[];
  label?: string;
  tagType: TagType;
} & ControllerFieldState;

export const TagPicker: React.FC<Props> = forwardRef(function TagPicker(
  { onChange, disabled, value, label, tagType },
  ref
) {
  const { tenantId } = useTenant();
  return (
    <EncampAutocomplete
      query={TAGS_QUERY}
      variables={{
        search: `tenantId:${tenantId} type:${tagType}`,
      }}
      getItems={(data: TagsQuery) => data.tags?.items ?? []}
      value={value || []}
      getOptionLabel={(tag) => (typeof tag === "string" ? tag : tag.name)}
      onChange={(value) => {
        // new tags will only be a string, existing tags will be of type `TagPickerFragment`
        const tags = value.map((tag) =>
          typeof tag === "string"
            ? ({
                __typename: "Tag",
                id: uuidv4(),
                name: tag,
              } satisfies TagPickerFragment)
            : tag
        );

        // remove duplicates with case insensitivity
        const uniqueTags = uniqBy(tags, (tag) => tag.name.toLowerCase());

        onChange(uniqueTags);
      }}
      label={label}
      multiple
      disabled={disabled}
      freeSolo
      hideErrors
      limitTags={3}
      getOptionDisabled={(tag) =>
        !!value?.map((tag) => tag.id).includes(tag.id)
      }
    />
  );
});
